/**
 * Shared utilities for V7-style annotation bounding boxes.
 *
 * Used by:
 *  - ImportLinksInlinePanel (import preview boxes)
 *  - markupsPopup (saved/post-import link boxes)
 *
 * Each component stores its own `boxElements[]` array and camera handler reference.
 * This module provides stateless helper functions that operate on those arrays.
 */

import { LINK_TYPE_COLORS } from '@/constants/linkTypeColors';

const THREE = window.THREE;
const Autodesk = window.Autodesk;

/**
 * Create a V7-style annotation box DOM element from world-space bounds.
 *
 * @param {Object} viewer        - Forge viewer instance (for worldToClient)
 * @param {Object} worldBounds   - { minX, maxX, minY, maxY }
 * @param {Object} options
 * @param {string} options.id          - Unique identifier (item.id or link.id)
 * @param {string} options.name        - Display label text
 * @param {string} options.type        - Link type ('P&ID', 'Equipment', etc.)
 * @param {string} [options.matchStatus] - 'matched'|'partial'|'unmatched' (import preview only)
 * @param {string} [options.boxSource]   - 'import-preview' or 'saved' (for orphan scoping)
 * @param {boolean} [options.clickable]  - Whether box responds to clicks (default false)
 * @param {Function} [options.onClick]   - Click handler (if clickable)
 * @returns {HTMLElement|null} The created box element, or null if screen projection fails
 */
export function createAnnotationBoxElement(viewer, worldBounds, options) {
  const { minX, maxX, minY, maxY } = worldBounds;
  const { id, name, type, matchStatus, boxSource, clickable, onClick } = options;

  // Convert world corners to screen pixels
  const screenTopLeft = viewer.worldToClient(new THREE.Vector3(minX, maxY, 0));
  const screenBottomRight = viewer.worldToClient(new THREE.Vector3(maxX, minY, 0));

  if (!screenTopLeft || !screenBottomRight) return null;

  const screenW = Math.abs(screenBottomRight.x - screenTopLeft.x);
  const screenH = Math.abs(screenBottomRight.y - screenTopLeft.y);
  const screenLeft = Math.min(screenTopLeft.x, screenBottomRight.x);
  const screenTop = Math.min(screenTopLeft.y, screenBottomRight.y);

  const boxColor = LINK_TYPE_COLORS[type] || LINK_TYPE_COLORS.default;
  const isMatched = !matchStatus || matchStatus === 'matched' || matchStatus === 'partial';
  const unmatchedClass = isMatched ? '' : ' v7-annotation-box--unmatched';
  const pointerEvents = clickable ? 'auto' : 'none';
  const cursor = clickable ? 'cursor:pointer;' : '';

  const box = document.createElement('div');
  box.className = `v7-annotation-box${unmatchedClass}`;
  box.style.cssText = `position:absolute;left:${screenLeft}px;top:${screenTop}px;width:${screenW}px;height:${screenH}px;pointer-events:${pointerEvents};z-index:51;${cursor}--v7-box-border:${boxColor};--v7-box-bg:${boxColor}1F;--v7-box-label-bg:${boxColor}D9;`;

  // Data attributes for identification and camera tracking
  box.setAttribute('data-id', String(id));
  box.setAttribute('data-tag-type', type);
  box.setAttribute('data-world-min-x', minX);
  box.setAttribute('data-world-max-x', maxX);
  box.setAttribute('data-world-min-y', minY);
  box.setAttribute('data-world-max-y', maxY);

  if (matchStatus) {
    box.setAttribute('data-match-status', matchStatus);
  }
  if (boxSource) {
    box.setAttribute('data-box-source', boxSource);
  }

  // Click handler
  if (clickable && onClick) {
    box.addEventListener('click', onClick);
  }

  // Label
  const label = document.createElement('span');
  label.className = 'v7-annotation-box__label';
  label.textContent = name;
  box.appendChild(label);

  return box;
}

/**
 * Update screen positions of all tracked box elements on camera change.
 * Reads stored world coordinates from data-* attributes.
 *
 * @param {Object} viewer       - Forge viewer instance
 * @param {HTMLElement[]} boxElements - Array of box DOM elements to update
 */
export function updateBoxPositions(viewer, boxElements) {
  if (!viewer) return;

  boxElements.forEach(box => {
    const worldMinX = parseFloat(box.getAttribute('data-world-min-x'));
    const worldMaxX = parseFloat(box.getAttribute('data-world-max-x'));
    const worldMinY = parseFloat(box.getAttribute('data-world-min-y'));
    const worldMaxY = parseFloat(box.getAttribute('data-world-max-y'));

    const screenTopLeft = viewer.worldToClient(new THREE.Vector3(worldMinX, worldMaxY, 0));
    const screenBottomRight = viewer.worldToClient(new THREE.Vector3(worldMaxX, worldMinY, 0));

    if (!screenTopLeft || !screenBottomRight) return;

    const screenW = Math.abs(screenBottomRight.x - screenTopLeft.x);
    const screenH = Math.abs(screenBottomRight.y - screenTopLeft.y);
    const screenLeft = Math.min(screenTopLeft.x, screenBottomRight.x);
    const screenTop = Math.min(screenTopLeft.y, screenBottomRight.y);

    box.style.left = `${screenLeft}px`;
    box.style.top = `${screenTop}px`;
    box.style.width = `${screenW}px`;
    box.style.height = `${screenH}px`;
  });
}

/**
 * Remove all tracked box elements, unregister camera handler, and sweep orphans.
 *
 * @param {HTMLElement[]} elements      - Array of tracked box DOM elements (will be emptied)
 * @param {Object} viewer               - Forge viewer instance
 * @param {Function|null} cameraHandler - The camera event handler to unregister
 * @param {string} [boxSource]          - If provided, only sweep orphans with this data-box-source
 * @returns {{ elements: HTMLElement[], cameraHandler: null }}
 *   New values to assign back to the caller's tracking variables
 */
export function cleanupBoxElements(elements, viewer, cameraHandler, boxSource) {
  // Remove tracked elements
  elements.forEach(el => { if (el) el.remove(); });

  // Unregister camera listener
  if (cameraHandler && viewer) {
    viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, cameraHandler);
  }

  // Sweep orphaned boxes (safety net)
  if (viewer?.container) {
    const selector = boxSource
      ? `.v7-annotation-box[data-box-source="${boxSource}"]`
      : '.v7-annotation-box';
    const orphans = viewer.container.querySelectorAll(selector);
    orphans.forEach(el => el.remove());
  }

  return { elements: [], cameraHandler: null };
}
